'use client';

import React, { memo, useMemo } from 'react';
import { cn } from '@/lib/utils';
import AppIcon from './AppIcon';
import AppImage from './AppImage';

interface AppLogoProps {
  src?: string; // Image source (optional)
  text?: string; // Logo text (optional)
  iconName?: string; // Icon name when no image
  size?: number; // Size for icon/image (usado quando responsiveClassName não é passado)
  /**
   * Tamanho responsivo no `img` (Tailwind). Preferir só **`h-*`** em breakpoints;
   * a largura segue a proporção da arte (`w-auto`), salvo se você passar algum utilitário `w-*`.
   */
  responsiveClassName?: string;
  /**
   * Com `responsiveClassName`: mesclado no `img` junto com `object-contain` / `w-auto` (quando couber).
   * Sem `responsiveClassName`: aplicado ao container `flex` (ou ao `img` no modo tamanho fixo).
   */
  className?: string;
  onClick?: () => void; // Click handler
}

const AppLogo = memo(function AppLogo({
  src = '/assets/images/app_logo.png',
  text,
  iconName = 'SparklesIcon',
  size = 64,
  responsiveClassName,
  className = '',
  onClick,
}: AppLogoProps) {
  const containerClassName = useMemo(
    () =>
      cn(
        'flex items-center gap-2',
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        !responsiveClassName && className
      ),
    [onClick, className, responsiveClassName]
  );

  const responsiveUsesWidthUtility =
    responsiveClassName != null && /\bw-/.test(responsiveClassName);

  const imageContent = src ? (
    responsiveClassName ? (
      <AppImage
        src={src}
        alt="Logo"
        className={cn(
          'block shrink-0 object-contain',
          !responsiveUsesWidthUtility && 'w-auto max-w-full',
          responsiveClassName,
          className
        )}
        priority
        unoptimized={src.endsWith('.svg')}
      />
    ) : (
      <AppImage
        src={src}
        alt="Logo"
        width={size}
        height={size}
        className={cn('flex-shrink-0', className)}
        priority
        unoptimized={src.endsWith('.svg')}
      />
    )
  ) : (
    <AppIcon name={iconName} size={size} className="flex-shrink-0" />
  );

  return (
    <div className={containerClassName} onClick={onClick}>
      {imageContent}
      {text && <span className="text-xl font-bold">{text}</span>}
    </div>
  );
});

export default AppLogo;